import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, UploadCloud } from 'lucide-react';
import { toast } from 'react-toastify';
import { getApiUrl } from '@/lib/api';

interface Profile {
  id: string;
  name: string;
}

export interface FileInfo {
    name: string;
    // Add other relevant info like size, upload date if available from backend
}

export interface ProfileFiles {
    projectFiles: FileInfo[];
    cvFiles: FileInfo[];
    catalogFiles: FileInfo[];
}

interface FirmenprofilSectionProps {
  profiles: Profile[];
  selectedProfileId: string | null;
  onSelectProfile: (id: string | null) => void;
  onCreateProfile: (name: string) => Promise<Profile | null>;
}

const FirmenprofilSection: React.FC<FirmenprofilSectionProps> = ({
  profiles,
  selectedProfileId,
  onSelectProfile,
  onCreateProfile,
}) => {
  const [newProfileName, setNewProfileName] = useState('');
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [profileFiles, setProfileFiles] = useState<ProfileFiles | null>(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isUploading, setIsUploading] = useState<Partial<Record<keyof ProfileFiles, boolean>>>({});

  const fetchFilesForProfile = async (profileId: string) => {
    setIsLoadingFiles(true);
    setProfileFiles(null); // Clear previous files
    try {
      const apiUrl = getApiUrl(`/api/profiles/${profileId}/files`);
      console.log(`Fetching files from: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to fetch files: ${response.statusText}`);
      }
      const files: ProfileFiles = await response.json();
      setProfileFiles(files);
    } catch (error) {
      toast.error(`Fehler beim Laden der Profildateien: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
      console.error("Error fetching profile files:", error);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  useEffect(() => {
    if (selectedProfileId) {
      fetchFilesForProfile(selectedProfileId);
    } else {
      setProfileFiles(null); // Clear files if no profile is selected
    }
    // Intentionally disable exhaustive-deps, fetchFilesForProfile is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [selectedProfileId]);

  const handleCreateClick = async () => {
    if (!newProfileName.trim()) {
      toast.warn('Bitte geben Sie einen Namen für das neue Profil ein.');
      return;
    }
    setIsCreatingProfile(true);
    const newProfile = await onCreateProfile(newProfileName);
    if (newProfile) {
       setNewProfileName(''); // Clear input on success
    }
    setIsCreatingProfile(false);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, fileType: keyof ProfileFiles) => {
      if (!selectedProfileId) {
          toast.error("Bitte wählen Sie zuerst ein Profil aus.");
          return;
      }
      if (!e.target.files || e.target.files.length === 0) return;

      const file = e.target.files[0];
      setIsUploading(prev => ({ ...prev, [fileType]: true }));

      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);

      try {
          const apiUrl = getApiUrl(`/api/profiles/${selectedProfileId}/files`);
          console.log(`Uploading file to: ${apiUrl}`);
          
          const response = await fetch(apiUrl, {
              method: 'POST',
              body: formData,
          });

          if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.detail || `Upload failed: ${response.statusText}`);
          }
          
          // Refresh files for the current profile after successful upload
          await fetchFilesForProfile(selectedProfileId);
          toast.success(`${fileType === 'projectFiles' ? 'Projektdokument' : fileType === 'cvFiles' ? 'Team-CV' : 'Produktkatalog'} "${file.name}" erfolgreich hochgeladen.`);
          
      } catch (error) {
          console.error(`Error uploading file for ${fileType}:`, error);
          toast.error(`Fehler beim Hochladen von ${file.name}: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
      } finally {
         setIsUploading(prev => ({ ...prev, [fileType]: false }));
         e.target.value = ''; // Clear the input
      }
  };

  const handleDeleteClick = async (fileType: keyof ProfileFiles, fileName: string) => {
      if (!selectedProfileId) return;

      if (!window.confirm(`Sind Sie sicher, dass Sie die Datei "${fileName}" löschen möchten? Dieses kann nicht rückgängig gemacht werden.`)) {
          return;
      }

      try {
          const url = new URL(getApiUrl(`/api/profiles/${selectedProfileId}/files`));
          url.searchParams.append('file_type', fileType);
          url.searchParams.append('file_name', fileName);

          console.log(`Deleting file using: ${url.toString()}`);
          
          const response = await fetch(url.toString(), {
              method: 'DELETE',
          });

          if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.detail || `Deletion failed: ${response.statusText}`);
          }
          
          // Refresh files after successful deletion
          await fetchFilesForProfile(selectedProfileId);
          toast.success(`Datei "${fileName}" erfolgreich gelöscht.`);

      } catch (error) {
          console.error(`Error deleting file ${fileName} for ${fileType}:`, error);
          toast.error(`Fehler beim Löschen von "${fileName}": ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
      }
  }

  const renderFileUploadSection = (
      title: string,
      fileType: keyof ProfileFiles,
      accept: string
    ) => (
      <div className="border border-dashed rounded-lg p-4 space-y-3">
        <h4 className="text-md font-medium">{title}</h4>
        {isLoadingFiles ? (
            <p className="text-sm text-muted-foreground">Lade Dateien...</p>
        ) : profileFiles && profileFiles[fileType]?.length > 0 ? (
          <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
            {profileFiles[fileType].map((file, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-lg hover:bg-muted/80">
                <span className="text-sm truncate max-w-[calc(100%-40px)]">{file.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteClick(fileType, file.name)}
                  title={`Datei ${file.name} löschen`}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Keine Dateien hochgeladen.</p>
        )}
        <div className="text-center mt-2">
          <Input
            type="file"
            className="hidden"
            id={`${fileType}-upload`}
            accept={accept}
            onChange={(e) => handleFileChange(e, fileType)}
            disabled={!selectedProfileId || isUploading[fileType]}
          />
          <Button asChild variant="outline" size="sm" disabled={!selectedProfileId || isUploading[fileType]}>
            <label htmlFor={`${fileType}-upload`} className={`cursor-pointer ${!selectedProfileId || isUploading[fileType] ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <UploadCloud className="h-4 w-4 mr-2" />
              {isUploading[fileType] ? 'Lädt hoch...' : 'Datei hochladen'}
            </label>
          </Button>
           {!selectedProfileId && <p className="text-xs text-muted-foreground mt-1">Wählen Sie ein Profil zum Hochladen aus.</p>}
        </div>
      </div>
  );

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Firmenprofile verwalten</h2>

      {/* Profile Selection and Creation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div className="space-y-4 border rounded-lg p-4">
          <h3 className="text-lg font-semibold">Profil auswählen</h3>
          {profiles.length > 0 ? (
             <select
               value={selectedProfileId ?? ''}
               onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onSelectProfile(e.target.value || null)}
               className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
             >
               <option value="" disabled>Profil auswählen...</option>
               {profiles.map(profile => (
                 <option key={profile.id} value={profile.id}>
                   {profile.name}
                 </option>
               ))}
             </select>
          ) : (
            <p className="text-sm text-muted-foreground">Noch keine Profile vorhanden.</p>
          )}
        </div>

        <div className="space-y-4 border rounded-lg p-4">
           <h3 className="text-lg font-semibold">Neues Profil erstellen</h3>
           <div className="flex gap-2">
             <Input
               type="text"
               value={newProfileName}
               onChange={(e) => setNewProfileName(e.target.value)}
               placeholder="Name des neuen Profils"
               disabled={isCreatingProfile}
             />
             <Button onClick={handleCreateClick} disabled={isCreatingProfile || !newProfileName.trim()}>
               {isCreatingProfile ? 'Erstelle...' : 'Erstellen'}
             </Button>
           </div>
         </div>
      </div>

      {/* File Uploads for Selected Profile */}
      {selectedProfileId && (
        <div className="border rounded-lg p-4 mt-6">
           <h3 className="text-xl font-semibold mb-4">Dokumente für Profil: {profiles.find(p => p.id === selectedProfileId)?.name}</h3>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             {renderFileUploadSection('Projektdokumente', 'projectFiles', '.pdf')}
             {renderFileUploadSection('Team-CVs', 'cvFiles', '.pdf')}
             {renderFileUploadSection('Produktkatalog', 'catalogFiles', '.pdf')}
           </div>
        </div>
      )}
       {!selectedProfileId && profiles.length > 0 && (
          <p className="text-center text-muted-foreground mt-6">Bitte wählen Sie oben ein Profil aus, um dessen Dokumente zu verwalten.</p>
       )}

    </div>
  );
};

export default FirmenprofilSection; 